import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { GoogleAnalyticsService } from '@buyiq-core/analytics/google-analytics.service';
import { AuthService } from '@buyiq-core/auth/auth.service';
import { NavigationState } from '@buyiq-core/models/navigation';
import { User } from '@buyiq-core/models/user';
import { NavigationService } from '@buyiq-core/navigation/navigation.service';
import { routeParts } from '@buyiq-core/route-parts';
import { UserService } from '@buyiq-core/user/user.service';
import { environment } from '@buyiq-environments/environment';
import { distinctUntilChanged, filter, map, skipWhile, switchMap } from 'rxjs/operators';
import { analyticsDefaultDimensionKey, AnalyticsDimension, AnalyticsDimensionType, AnalyticsEvent } from '../models/analytics';
import { DatadogRumService } from './datadog-rum.service';
import { IAnalyticsService } from '@cia-front-end-apps/shared/analytics/analytics';

@Injectable({
    providedIn: 'root'
})
export class AnalyticsService implements IAnalyticsService {
    private ignoredRoutes = [
        `/${routeParts.logout}`
    ];

    private isAuthenticated = false;
    private customDimensions: Array<AnalyticsDimension>;

    constructor(
        private navigationService: NavigationService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private datadogRumService: DatadogRumService,
        private userService: UserService,
        private authService: AuthService,
        @Inject(PLATFORM_ID) private readonly platformId: unknown
    ) {
    }

    initialize(): void {
        if (isPlatformBrowser(this.platformId)) {
            this.googleAnalyticsService.initialize(environment.googleAnalyticsId, environment.ga4MeasurementId);
            this.datadogRumService.initialize();
        }

        this.authService.getLoginState()
            .pipe(
                distinctUntilChanged(),
                skipWhile(isAuthenticated => !isAuthenticated)
            )
            .subscribe(isAuthenticated => {
                this.isAuthenticated = isAuthenticated;
            });

        this.navigationService.currentState
            .pipe(
                filter(() => this.isAuthenticated),
                filter((state: NavigationState) => {
                    return state.sanitizedUrl && !this.ignoredRoutes.includes(state.sanitizedUrl);
                }),
                switchMap((state: NavigationState) => {
                    return this.userService.getCurrentUser()
                        .pipe(
                            map(user => {
                                const pageUrl = state.sanitizedUrl;
                                const userId = user.id;
                                this.trackPageView(pageUrl, userId);
                                this.datadogRumService.setUser(user);
                            })
                        );
                })
            )
            .subscribe();
    }

    trackEvent(event: AnalyticsEvent): void {
        this.userService.getCurrentUser()
            .subscribe(user => {
                const customDimensions = this.getCustomDimensions(user);
                this.googleAnalyticsService.trackEvent(event, customDimensions);
            });
    }

    private trackPageView(pageUrl: string, userId: number): void {
        this.googleAnalyticsService.trackPageView(pageUrl);
    }

    private getCustomDimensions(user: User): Array<AnalyticsDimension> {
        let dimensions = this.customDimensions;

        if (!this.customDimensions) {
            dimensions = [
                new AnalyticsDimension({
                    type: AnalyticsDimensionType.UserId,
                    key: analyticsDefaultDimensionKey.get(AnalyticsDimensionType.UserId),
                    value: `${user.id}`
                }),
                new AnalyticsDimension({
                    type: AnalyticsDimensionType.BuildVersion,
                    key: analyticsDefaultDimensionKey.get(AnalyticsDimensionType.BuildVersion),
                    value: environment.buildVersion.includes('{') ? 'developer' : environment.buildVersion
                }),
                new AnalyticsDimension({
                    type: AnalyticsDimensionType.ChainStoreId,
                    key: analyticsDefaultDimensionKey.get(AnalyticsDimensionType.ChainStoreId),
                    value: `${user.currentStore?.id}`
                })
            ];
        }

        return dimensions;
    }
}
